import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import OtpInput, { emptyOtp, isOtpComplete, otpCode } from '../../../../shared/ui/OtpInput';
import {
  AUTH_ERROR_CODE,
  AUTH_ERROR_MESSAGE,
  DEFAULT_COUNTRY,
  OTP_CHANNEL,
  OTP_SUPPORTED_COUNTRIES,
  OTP_LENGTH,
  OTP_RESEND_SECONDS,
  WHATSAPP_OTP_ENABLED,
} from '../../constants';
import {
  EMAIL_VERIFICATION_COLOR as COLOR,
  EMAIL_VERIFICATION_METRICS as METRICS,
  formatCountdown,
} from '../EmailVerification/emailVerification.constants';
import AuthPhoneField, { AuthPhoneValue } from '../AuthModal/AuthPhoneField';
import { AUTH_MODAL_LAYOUT } from '../AuthModal/authModal.constants';
import VerificationDialog, { primaryButtonSx, textLinkSx, VerificationTitle } from '../VerificationDialog/VerificationDialog';
import {
  PHONE_CHANGE_COPY as COPY,
  PHONE_CHANGE_STEP as STEP,
  PhoneChangeStep,
} from './phoneChange.constants';

export type PhoneChangeModalProps = {
  open: boolean;
  onClose?: () => void;
  /** Runs once the new number is saved, just before the dialog closes. */
  onChanged?: (phone: string) => void;
  /** Send a code to the new number. Reject to show the server's message (taken, same number, limit…). */
  onSendCode?: (phone: string, channel: string) => Promise<unknown>;
  /** Check the code and save the number. Reject for a wrong or expired code. */
  onVerifyCode?: (phone: string, code: string) => Promise<unknown>;
  /** Offer the WhatsApp switch on the code step. Defaults to REACT_APP_WHATSAPP_LOGIN_OTP_ENABLED. */
  whatsappEnabled?: boolean;
};

const PHONE_DIGITS = 10;

/** Failures the user can act on keep the server's message; anything else gets the fallback copy. */
const SHOWN_ERROR_CODES: string[] = [
  AUTH_ERROR_CODE.INVALID_PHONE,
  AUTH_ERROR_CODE.SAME_PHONE,
  AUTH_ERROR_CODE.PHONE_TAKEN,
  AUTH_ERROR_CODE.PHONE_CHANGE_LIMIT,
  AUTH_ERROR_CODE.INVALID_OTP,
  AUTH_ERROR_CODE.OTP_EXPIRED,
  AUTH_ERROR_CODE.TOO_MANY_ATTEMPTS,
  AUTH_ERROR_CODE.WHATSAPP_OTP_DISABLED,
];

/** The number itself is the problem: the user has to pick another one on the first step. */
const NUMBER_ERROR_CODES: string[] = [
  AUTH_ERROR_CODE.SAME_PHONE,
  AUTH_ERROR_CODE.PHONE_TAKEN,
  AUTH_ERROR_CODE.PHONE_CHANGE_LIMIT,
];

function errorCode(error: unknown) {
  return (error as { code?: string } | null)?.code || '';
}

function errorText(error: unknown, fallback: string) {
  const coded = error as { code?: string; message?: string } | null;
  if (coded && coded.code && coded.message && SHOWN_ERROR_CODES.includes(coded.code)) return coded.message;
  return fallback;
}

type PhoneChangeModalState = {
  step: PhoneChangeStep;
  phone: string;
  /** ISO code from the dial-code picker; only OTP_SUPPORTED_COUNTRIES can get a code. */
  countryCode: string;
  channel: string;
  /** False once the server said WhatsApp is switched off. */
  whatsappAvailable: boolean;
  digits: string[];
  busy: boolean;
  errorMessage: string;
  secondsLeft: number;
  sendKey: number;
};

const INITIAL_STATE: PhoneChangeModalState = {
  step: STEP.PHONE,
  phone: '',
  countryCode: DEFAULT_COUNTRY,
  channel: OTP_CHANNEL.SMS,
  whatsappAvailable: true,
  digits: emptyOtp(OTP_LENGTH),
  busy: false,
  errorMessage: '',
  secondsLeft: 0,
  sendKey: 0,
};

/**
 * New mobile → 6-digit code sent to it → saved, dialog closes. Indian numbers only, like login.
 * The server refuses the current number and numbers on other accounts, and never merges.
 */
class PhoneChangeModal extends React.Component<PhoneChangeModalProps, PhoneChangeModalState> {
  state: PhoneChangeModalState = { ...INITIAL_STATE, digits: emptyOtp(OTP_LENGTH) };

  private timer: ReturnType<typeof setInterval> | null = null;

  componentWillUnmount() {
    this.stopTimer();
  }

  whatsappOffered() {
    const enabled = this.props.whatsappEnabled === undefined ? WHATSAPP_OTP_ENABLED : this.props.whatsappEnabled;
    return enabled && this.state.whatsappAvailable;
  }

  startTimer() {
    this.stopTimer();
    this.setState({ secondsLeft: OTP_RESEND_SECONDS });
    this.timer = setInterval(() => {
      this.setState((current) => {
        if (current.secondsLeft <= 1) this.stopTimer();
        return { secondsLeft: Math.max(current.secondsLeft - 1, 0) };
      });
    }, 1000);
  }

  stopTimer() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  handleExited = () => {
    this.stopTimer();
    this.setState({ ...INITIAL_STATE, digits: emptyOtp(OTP_LENGTH) });
  };

  handleClose = () => {
    if (this.props.onClose) this.props.onClose();
  };

  /** Same field as the login screen: a pasted "+44 …" also moves the picker. */
  handlePhoneField = (value: AuthPhoneValue) => {
    this.setState({ ...value, errorMessage: '' });
  };

  /** A failed send on the code step: a number problem goes back to the number; a WhatsApp refusal hides the switch. */
  sendFailure(error: unknown): Partial<PhoneChangeModalState> & { errorMessage: string } {
    const code = errorCode(error);
    const errorMessage = errorText(error, COPY.sendFailed);
    if (code === AUTH_ERROR_CODE.WHATSAPP_OTP_DISABLED) {
      return { errorMessage, whatsappAvailable: false, channel: OTP_CHANNEL.SMS };
    }
    if (NUMBER_ERROR_CODES.includes(code)) {
      this.stopTimer();
      return { errorMessage, step: STEP.PHONE, secondsLeft: 0 };
    }
    return { errorMessage };
  }

  handleSend = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    if (this.state.phone.length !== PHONE_DIGITS || this.state.busy) return;
    // Like Get OTP on the login screen: other countries are stopped before any request.
    if (!OTP_SUPPORTED_COUNTRIES.includes(this.state.countryCode)) {
      this.setState({ errorMessage: AUTH_ERROR_MESSAGE[AUTH_ERROR_CODE.UNSUPPORTED_COUNTRY] });
      return;
    }
    this.setState({ busy: true, errorMessage: '' });
    try {
      if (this.props.onSendCode) await this.props.onSendCode(this.state.phone, OTP_CHANNEL.SMS);
      this.setState((current) => ({
        step: STEP.OTP,
        channel: OTP_CHANNEL.SMS,
        busy: false,
        digits: emptyOtp(OTP_LENGTH),
        sendKey: current.sendKey + 1,
      }));
      this.startTimer();
    } catch (error) {
      this.setState({ busy: false, errorMessage: errorText(error, COPY.sendFailed) });
    }
  };

  /** Resend on the current channel, or switch channel; both restart the 30 s timer. */
  resendOn = async (channel: string) => {
    if (this.state.busy) return;
    this.setState((current) => ({
      busy: true,
      channel,
      digits: emptyOtp(OTP_LENGTH),
      errorMessage: '',
      sendKey: current.sendKey + 1,
    }));
    try {
      if (this.props.onSendCode) await this.props.onSendCode(this.state.phone, channel);
      this.setState({ busy: false });
      this.startTimer();
    } catch (error) {
      const failure = this.sendFailure(error);
      this.setState((current) => ({
        busy: false,
        errorMessage: failure.errorMessage,
        step: failure.step ?? current.step,
        secondsLeft: failure.secondsLeft ?? current.secondsLeft,
        channel: failure.channel ?? current.channel,
        whatsappAvailable: failure.whatsappAvailable ?? current.whatsappAvailable,
      }));
    }
  };

  handleResend = () => {
    if (this.state.secondsLeft > 0) return;
    this.resendOn(this.state.channel);
  };

  handleSwitchChannel = () => {
    this.resendOn(this.state.channel === OTP_CHANNEL.WHATSAPP ? OTP_CHANNEL.SMS : OTP_CHANNEL.WHATSAPP);
  };

  handleChangeNumber = () => {
    this.stopTimer();
    this.setState({ step: STEP.PHONE, errorMessage: '', secondsLeft: 0 });
  };

  handleDigits = (digits: string[]) => {
    this.setState({ digits, errorMessage: '' }, () => {
      if (isOtpComplete(digits)) this.handleVerify();
    });
  };

  handleVerify = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    if (!isOtpComplete(this.state.digits) || this.state.busy) return;
    const { phone } = this.state;
    this.setState({ busy: true, errorMessage: '' });
    try {
      if (this.props.onVerifyCode) await this.props.onVerifyCode(phone, otpCode(this.state.digits));
    } catch (error) {
      if (NUMBER_ERROR_CODES.includes(errorCode(error))) {
        // Taken or limit reached since the code was sent: pick another number.
        this.stopTimer();
        this.setState({ busy: false, step: STEP.PHONE, secondsLeft: 0, errorMessage: errorText(error, COPY.sendFailed) });
        return;
      }
      this.setState({ busy: false, errorMessage: errorText(error, COPY.wrongCode) });
      return;
    }
    this.stopTimer();
    this.setState({ busy: false });
    if (this.props.onChanged) this.props.onChanged(phone);
    if (this.props.onClose) this.props.onClose();
  };

  renderPhoneStep() {
    const { phone, busy, errorMessage } = this.state;
    return (
      <Box component="form" onSubmit={this.handleSend} noValidate>
        <VerificationTitle title={COPY.phoneTitle} subtitle={COPY.phoneSubtitle} />
        <Box sx={{ mb: 1 }}>
          <AuthPhoneField
            layout={AUTH_MODAL_LAYOUT.DESKTOP}
            id="phone-change-number"
            label={COPY.phoneLabel}
            phone={phone}
            countryCode={this.state.countryCode}
            onChange={this.handlePhoneField}
            disabled={busy}
            autoFocus
          />
        </Box>
        {errorMessage ? (
          <Box component="p" role="alert" sx={{ m: 0, mb: 1, fontSize: 13, color: COLOR.error }}>
            {errorMessage}
          </Box>
        ) : null}
        <Box component="p" sx={{ m: 0, mb: 2, fontSize: 12.5, color: COLOR.muted }}>
          {COPY.signOutNote}
        </Box>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={phone.length !== PHONE_DIGITS || busy}
          sx={primaryButtonSx}
        >
          {busy ? <CircularProgress size={22} sx={{ color: COLOR.onBrand }} /> : COPY.sendCode}
        </Button>
      </Box>
    );
  }

  renderOtpStep() {
    const { errorMessage, secondsLeft, busy, channel } = this.state;
    return (
      <Box component="form" onSubmit={this.handleVerify} noValidate>
        <VerificationTitle title={COPY.otpTitle} subtitle={COPY.otpSubtitle} />
        {/* Same field as the number step, read-only, with Change to edit it. */}
        <Box sx={{ mb: 2.5 }}>
          <AuthPhoneField
            layout={AUTH_MODAL_LAYOUT.DESKTOP}
            id="phone-change-sent-to"
            label={COPY.phoneLabel}
            phone={this.state.phone}
            countryCode={this.state.countryCode}
            onChange={this.handlePhoneField}
            readOnly
            endAction={(
              <Link component="button" type="button" underline="hover" onClick={this.handleChangeNumber} sx={textLinkSx}>
                {COPY.change}
              </Link>
            )}
          />
        </Box>

        <Box component="p" id="phone-change-otp-label" sx={{ m: 0, mb: 1, fontSize: 13, fontWeight: 600, color: COLOR.text }}>
          {COPY.otpLabel}
        </Box>
        <OtpInput
          value={this.state.digits}
          onChange={this.handleDigits}
          labelledBy="phone-change-otp-label"
          focusKey={this.state.sendKey}
          error={Boolean(errorMessage)}
          disabled={busy}
          boxHeight={METRICS.otpBoxHeight}
          gap={METRICS.otpGap}
          radius={METRICS.otpRadius}
          digitSx={{ fontSize: 20, fontWeight: 600 }}
          colors={{ field: COLOR.field, active: COLOR.brand, text: COLOR.text, error: COLOR.error }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.25, mb: 2.5, minHeight: 20 }}>
          {errorMessage ? (
            <Box component="p" role="alert" sx={{ m: 0, fontSize: 12.5, color: COLOR.error }}>
              {errorMessage}
            </Box>
          ) : secondsLeft > 0 ? (
            <Box component="p" sx={{ m: 0, fontSize: 12.5, color: COLOR.muted }}>
              {COPY.resendIn(formatCountdown(secondsLeft))}
            </Box>
          ) : (
            <span />
          )}
          {secondsLeft === 0 ? (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={this.handleResend}
              disabled={busy}
              sx={{ ...textLinkSx, fontSize: 12.5 }}
            >
              {COPY.resend}
            </Link>
          ) : null}
        </Box>

        {this.whatsappOffered() ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={this.handleSwitchChannel}
              disabled={busy}
              sx={{ ...textLinkSx, fontSize: 12.5 }}
            >
              {channel === OTP_CHANNEL.WHATSAPP ? COPY.sms : COPY.whatsapp}
            </Link>
          </Box>
        ) : null}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!isOtpComplete(this.state.digits) || busy}
          sx={primaryButtonSx}
        >
          {busy ? <CircularProgress size={22} sx={{ color: COLOR.onBrand }} /> : COPY.verify}
        </Button>
      </Box>
    );
  }

  render() {
    const { step, busy } = this.state;
    return (
      <VerificationDialog
        open={this.props.open}
        busy={busy}
        onClose={this.handleClose}
        onExited={this.handleExited}
        stepKey={step}
      >
        {step === STEP.PHONE ? this.renderPhoneStep() : null}
        {step === STEP.OTP ? this.renderOtpStep() : null}
      </VerificationDialog>
    );
  }
}

export default PhoneChangeModal;
